import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import {
  Users, ClipboardList, CalendarDays, BookOpen,
  MessageSquare, TrendingUp, ArrowRight, Loader2, Trophy,
} from "lucide-react";

function StatCard({ label, value, icon: Icon, accent, to, sublabel }) {
  const card = (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4 hover:shadow-md transition-shadow cursor-pointer"
      style={{ borderTopColor: accent, borderTopWidth: 3 }}
    >
      <div className="rounded-lg p-2.5 shrink-0" style={{ background: accent + "18" }}>
        <Icon size={20} style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-2xl font-bold text-slate-900">{value ?? "—"}</div>
        <div className="text-sm text-slate-500 mt-0.5">{label}</div>
        {sublabel && <div className="text-xs text-slate-400 mt-1">{sublabel}</div>}
      </div>
      {to && <ArrowRight size={16} className="text-slate-300 shrink-0 mt-1" />}
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

function QuickAction({ label, to, colour }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-medium transition-colors hover:shadow-sm"
      style={{ borderColor: colour + "40", background: colour + "0a", color: colour }}
    >
      {label}
      <ArrowRight size={14} />
    </Link>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminShell title="Dashboard">
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin text-slate-400" />
      </div>
    </AdminShell>
  );

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  };

  return (
    <AdminShell
      title={`${greeting()} 👋`}
      subtitle="Live overview for Genius Education"
      stats={stats}
    >
      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Students"
          value={stats?.students}
          icon={Users}
          accent="#2563eb"
          to="/admin/students"
          sublabel={`${stats?.parents || 0} parents`}
        />
        <StatCard
          label="Pending Signups"
          value={stats?.pending_signups}
          icon={ClipboardList}
          accent={stats?.pending_signups > 0 ? "#dc2626" : "#16a34a"}
          to="/admin/signups"
          sublabel={`${stats?.total_signups || 0} total`}
        />
        <StatCard
          label="Bookings"
          value={stats?.bookings}
          icon={CalendarDays}
          accent="#7c3aed"
          to="/admin/bookings"
          sublabel={`${stats?.pending_bookings || 0} pending`}
        />
        <StatCard
          label="Questions"
          value={stats?.questions?.toLocaleString()}
          icon={BookOpen}
          accent="#d97706"
          to="/admin/questions"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Where the real numbers are.
            This panel used to show "Class Average" and "Quizzes This Week",
            both counted from `quiz_attempts` - the practice quiz, which is
            switched off for students and holds no rows. So the first thing
            the tutor saw each morning was a 0% in red, on work nobody was
            ever able to do. The two pages linked below count marks against a
            mark scheme and days of the month finished, which is the work. */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-5">
            How they are doing
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/admin/progress"
              className="block bg-slate-50 hover:bg-slate-100 rounded-xl p-5 transition-colors">
              <div className="flex items-center gap-2 font-semibold text-slate-800 mb-1">
                <TrendingUp size={16} className="text-emerald-600" /> Progress
              </div>
              <p className="text-xs text-slate-500">
                Marks against the mark scheme, per child and per topic — tutor
                marks, automatic marks and self-marks counted separately.
              </p>
            </Link>
            <Link to="/admin/star"
              className="block bg-slate-50 hover:bg-slate-100 rounded-xl p-5 transition-colors">
              <div className="flex items-center gap-2 font-semibold text-slate-800 mb-1">
                <Trophy size={16} className="text-amber-500" /> Star of the month
              </div>
              <p className="text-xs text-slate-500">
                How much of the month each child finished, who has reached
                which stage, and where the coupon goes.
              </p>
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-4">
            {stats?.total_students ?? 0} student{stats?.total_students === 1 ? "" : "s"}
            {" · "}{stats?.total_parents ?? 0} parent{stats?.total_parents === 1 ? "" : "s"}
          </p>
        </div>

        {/* Quick actions */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-5">
            Quick Actions
          </h2>
          <div className="space-y-2">
            <QuickAction label="Review pending signups" to="/admin/signups" colour="#2563eb" />
            <QuickAction label="Confirm bookings" to="/admin/bookings" colour="#7c3aed" />
            <QuickAction label="Add a student or parent" to="/admin/students" colour="#16a34a" />
            <QuickAction label="Add a question" to="/admin/questions" colour="#d97706" />
            <QuickAction label="Read enquiries" to="/admin/enquiries" colour="#0891b2" />
          </div>

          {stats?.enquiries > 0 && (
            <div className="mt-4 flex items-center gap-2 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-xs text-cyan-700">
              <MessageSquare size={13} />
              {stats.enquiries} contact form {stats.enquiries === 1 ? "submission" : "submissions"}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
